import React from "react";
import { motion } from "framer-motion";

export const ADVISOR_PHOTO = "/biagio-cutout.webp";

const EASE = [0.32, 0.72, 0, 1];
const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: EASE, delay },
});

const scrollTo = (href) => (e) => {
  e.preventDefault();
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

function Portrait() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.1, ease: EASE, delay: 0.2 }}
      className="relative mx-auto w-full max-w-[520px] h-[380px] sm:h-[520px] lg:h-[620px]"
      data-testid="hero-portrait"
    >
      <div className="absolute inset-x-[4%] bottom-0 top-[18%] rounded-t-[999px] bg-gradient-to-b from-[#EEF1F7] to-[#F7F5EF] border border-b-0 border-[#E6E2D8]" />
      <img
        src={ADVISOR_PHOTO}
        alt="Biagio DiGirolamo, Founding Principal of Prospera Financial"
        className="relative block h-full w-auto mx-auto object-contain object-bottom select-none"
        draggable={false}
        style={{ filter: "drop-shadow(0 30px 40px rgba(11,31,63,0.16))" }}
      />
    </motion.div>
  );
}

export default function Hero() {
  return (
    <section
      id="top"
      className="relative overflow-hidden bg-white pt-28 md:pt-32"
      data-testid="hero-section"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-end">
        <div className="lg:col-span-7 lg:pb-24 pt-6 lg:pt-16">
          <motion.div
            {...fadeUp(0)}
            className="flex items-center gap-3 text-[10.5px] sm:text-[11px] tracking-[0.22em] sm:tracking-[0.3em] uppercase font-semibold text-[#8A6A2E]"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-8 bg-[#B08D57]" />
            Independent Advisory · Toronto
          </motion.div>

          <motion.h1
            {...fadeUp(0.1)}
            className="font-display text-[#0B1F3F] text-4xl sm:text-5xl xl:text-[64px] leading-[1.06] tracking-tight mt-6"
            data-testid="hero-headline"
          >
            They designed insurance to cost you a lifetime.{" "}
            <span className="italic text-[#8A6A2E]">I redesigned it</span> to
            give you one.
          </motion.h1>

          <motion.p
            {...fadeUp(0.2)}
            className="mt-6 max-w-lg text-base md:text-lg leading-relaxed text-[#5A6476]"
            data-testid="hero-subheadline"
          >
            A boutique advisory practice for families, founders, and
            executives who want advice that truly puts them first.
          </motion.p>

          <motion.div {...fadeUp(0.3)} className="mt-9 flex flex-wrap items-center gap-4">
            <button
              onClick={scrollTo("#contact")}
              className="btn-primary"
              data-testid="hero-cta-primary"
            >
              Book a consultation
            </button>
            <button
              onClick={scrollTo("#services")}
              className="btn-outline"
              data-testid="hero-cta-secondary"
            >
              Explore our solutions
            </button>
          </motion.div>

          <motion.div
            {...fadeUp(0.4)}
            className="mt-8 lg:mt-12 pt-6 border-t border-[#E6E2D8] max-w-md"
            data-testid="hero-founder"
          >
            <div className="font-display text-[#0B1F3F] text-lg">Biagio DiGirolamo</div>
            <div className="text-[13px] text-[#5A6476] mt-1">
              Founding Principal · Fiduciary · Fee-only
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <Portrait />
        </div>
      </div>
      <div className="h-px bg-[#E6E2D8]" />
    </section>
  );
}
